import { message } from 'antd'
import { useTranslation } from 'react-i18next'
import { getApi } from '@BUP/services/Axios'
import { monitorException } from '@BUP/services/monitor'
import { buildLegacyCsv, downloadCsv } from '@BUP/utils/csv'
import { resolveRosterGenders } from '@BUP/utils/courseGeneratorExport'
import { useCourseAssignmentMutation } from './useCourseAssignmentMutation'

/**
 * Downloads, for one period, the 4 semicolon-separated CSVs course-generator (the Python course
 * builder) reads as input. Names are course-generator's own (see its config/parameters.yaml) plus
 * a "-<escuela>-<grado>-<año>" suffix (see buildCourseGeneratorFileSuffix), so files of different
 * schools/grades/years don't collide - whoever runs course-generator renames them back, or points
 * its config at the suffixed names. 01-03 come from the backend's legacy export; 04 (gender) is
 * resolved here from the sociogram surveys, since that data only lives in the sociogram API.
 *
 * A gender lookup failure never blocks the download: 04 is still produced (blank genders, to be
 * completed by hand, same as course-generator's own convention for unknown values) and the
 * counselor is told how many of the period's courses got gender data.
 */
const downloadCourseGeneratorFiles = async ({ periodId, fileSuffix = '' }) => {
  const client = await getApi('schools', { authenticated: true, version: 5 })
  const [{ data: legacyExport }, { data: rosterData }] = await Promise.all([
    client.get(`/current-organization/course-assignment/periods/${periodId}/legacy-export`),
    client.get(`/current-organization/course-assignment/periods/${periodId}/roster`)
  ])
  const roster = rosterData?.roster ?? []

  let genders = { genderByEnrollmentId: new Map(), gradesTotal: 0, gradesResolved: 0 }
  let genderLookupFailed = false
  try {
    const sociogramClient = await getApi('sociogram', { authenticated: true })
    genders = await resolveRosterGenders({ roster, sociogramClient })
  } catch (error) {
    genderLookupFailed = true
    monitorException(new Error('Course-generator export: gender lookup failed', { cause: error }), {
      tags: { hook: 'useDownloadCourseGeneratorFiles' }
    })
  }

  const genderCsv = buildLegacyCsv(['Estudiante', 'Curso', 'Genero'], roster.map(student => ({
    Estudiante: student.name,
    Curso: student.courseName,
    Genero: genders.genderByEnrollmentId.get(student.enrollmentId) || ''
  })))

  downloadCsv(`01 Eleccion companeros${fileSuffix}.csv`, legacyExport.preferencesCsv)
  downloadCsv(`02 Evaluacion profesores${fileSuffix}.csv`, legacyExport.evaluationsCsv)
  downloadCsv(`03 Casos especiales${fileSuffix}.csv`, legacyExport.exclusionsCsv)
  downloadCsv(`04 Estudiantes genero${fileSuffix}.csv`, genderCsv)

  return { ...genders, genderLookupFailed }
}

/**
 * Which toast to show after the download: the files always download, so this only decides how
 * loudly to flag missing gender data. No course matched at all (lookup failed, or the roster has
 * no `grade:<uuid>` tags to match surveys by) means the whole Genero column is blank.
 * @returns {'downloadFilesSuccess'|'downloadFilesGenderPartial'|'downloadFilesGenderMissing'}
 */
export const resolveDownloadToastKey = ({ gradesTotal, gradesResolved, genderLookupFailed }) => {
  if (genderLookupFailed || gradesResolved === 0) return 'downloadFilesGenderMissing'
  if (gradesResolved < gradesTotal) return 'downloadFilesGenderPartial'
  return 'downloadFilesSuccess'
}

export const useDownloadCourseGeneratorFiles = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })

  return useCourseAssignmentMutation({
    mutationFn: downloadCourseGeneratorFiles,
    hookName: 'useDownloadCourseGeneratorFiles',
    errorMessage: 'Error downloading course-generator files',
    onSuccess: (result) => {
      const key = resolveDownloadToastKey(result)
      if (key === 'downloadFilesSuccess') {
        message.success(t(key), 4)
      } else {
        message.warning(t(key, { resolved: result.gradesResolved, total: result.gradesTotal }), 8)
      }
    }
  })
}
